import caveat700 from "src/assets/fonts/caveat-latin-700-normal.woff2";
import { removeBackground } from "src/pages/employer-certificate/certificateIssuer";

/**
 * Stamp tickets, notary seals and serial number printed on the affidavit
 * (Affidavit Stamp & Seal settings, GET /affidavit-stamp/get).
 */

export type StampTextPosition = { x: number; y: number; fontSize: number };

/**
 * Size and place of one ticket / seal, moved from where the template puts it
 * by default: x / y in mm (right / down), size in % of the default size.
 */
export type StampLayoutItem = { x: number; y: number; size: number };

export const STAMP_LAYOUT_KEYS = [
  "ticket10",
  "ticket5",
  "notaryLeft",
  "notaryRight",
  "noted",
  "attested",
] as const;

export type StampLayoutKey = typeof STAMP_LAYOUT_KEYS[number];
export type StampLayout = Record<StampLayoutKey, StampLayoutItem>;

export const DEFAULT_LAYOUT_ITEM: StampLayoutItem = { x: 0, y: 0, size: 100 };

/** The saved layout with every key present (missing = default place and size). */
export const fullLayout = (saved?: Partial<Record<string, StampLayoutItem>> | null): StampLayout =>
  STAMP_LAYOUT_KEYS.reduce((acc, key) => {
    acc[key] = { ...DEFAULT_LAYOUT_ITEM, ...(saved?.[key] || {}) };
    return acc;
  }, {} as StampLayout);

export type AffidavitStampSettings = {
  /** Notary ON / OFF. OFF: plain affidavit, none of the below is printed. */
  notaryEnabled?: boolean;
  ticket10Image?: string;
  ticket5Image?: string;
  notarySealImage?: string;
  notedSealImage?: string;
  attestedSealImage?: string;
  serialStart?: number;
  serialNext?: number;
  lastUsedSerial?: number;
  notedDatePos?: StampTextPosition;
  notedSerialPos?: StampTextPosition;
  attestedDatePos?: StampTextPosition;
  layout?: Partial<Record<string, StampLayoutItem>>;
};

/** POST /affidavit-stamp/reserve-serial, one application's number. */
export type AffidavitSerial = {
  serialNumber: number;
  /** "37248/26" */
  serialLabel: string;
  /** YYYY-MM-DD, the day before it was given out. */
  notedDate: string;
};

/** Everything the template prints, as base64 data-URLs ("" = not printed). */
export type AffidavitStamps = {
  ticket10: string;
  ticket5: string;
  notarySeal: string;
  notedSeal: string;
  attestedSeal: string;
  notedDatePos: StampTextPosition;
  notedSerialPos: StampTextPosition;
  attestedDatePos: StampTextPosition;
  layout: StampLayout;
};

/** Texts written on the seals, drawn as images (see stampText). */
export type AffidavitMarks = {
  /** "19 OCT 2026", rubber date stamp. */
  notedDate: string;
  /** "37248/26", handwritten. */
  serial: string;
  /** "19-10-2026", handwritten on the attested seal. */
  attestedDate: string;
  /**
   * "19 October 2026", the footer DATE. Same day as the noted seal (the day
   * before generation), so the two dates on the page always agree.
   */
  footerDate: string;
};

const DEFAULT_POS = {
  notedDatePos: { x: 30, y: 62, fontSize: 18 },
  notedSerialPos: { x: 38, y: 92, fontSize: 18 },
  attestedDatePos: { x: 22, y: 42, fontSize: 14 },
};

/** Stamp-pad violet, as on the reference affidavit. */
export const STAMP_INK = "#5b3a96";
/** Ball-pen blue the serial is written in. */
export const PEN_INK = "#2b3f9e";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/** "2026-10-19" → "19 OCT 2026". */
export const notedDateLabel = (notedDate?: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(notedDate || "");
  return m ? `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : "";
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "2026-10-19" → "19 October 2026". */
const footerDateLabel = (notedDate?: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(notedDate || "");
  return m ? `${Number(m[3])} ${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}` : "";
};

/** A remote image as a base64 data-URL, "" when it cannot be read. */
export const urlToDataUrl = async (url: string): Promise<string> => {
  if (!url) return "";
  if (url.startsWith("data:")) return url;
  try {
    const response = await fetch(url);
    if (!response.ok) return "";
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(typeof reader.result === "string" ? reader.result : "");
      reader.onerror = () => resolve("");
      reader.readAsDataURL(blob);
    });
  } catch {
    return "";
  }
};

/**
 * A seal with its paper removed, so it prints over the tickets and text
 * instead of as a white box (the PDF renderer ignores mix-blend-mode).
 */
const sealImage = async (url?: string) => {
  if (!url) return "";
  const data = await urlToDataUrl(url);
  if (!data) return "";
  return (await removeBackground(data)) || data;
};

/** Notary ON: tickets, seals and serial go on the affidavit, and the PAN form makes it. */
export const isNotaryOn = (settings?: AffidavitStampSettings | null) =>
  !!settings?.notaryEnabled;

/**
 * The settings' images, read into data-URLs so the posted HTML carries them
 * and the PDF renderer never has to reach the uploads server.
 */
export const loadAffidavitStamps = async (
  settings?: AffidavitStampSettings | null
): Promise<AffidavitStamps> => {
  // Notary OFF: no images, so the affidavit prints plain.
  if (!isNotaryOn(settings)) settings = null;
  const [ticket10, ticket5, notarySeal, notedSeal, attestedSeal] = await Promise.all([
    urlToDataUrl(settings?.ticket10Image || ""),
    urlToDataUrl(settings?.ticket5Image || ""),
    sealImage(settings?.notarySealImage),
    sealImage(settings?.notedSealImage),
    sealImage(settings?.attestedSealImage),
  ]);
  return {
    ticket10,
    ticket5,
    notarySeal,
    notedSeal,
    attestedSeal,
    notedDatePos: settings?.notedDatePos || DEFAULT_POS.notedDatePos,
    notedSerialPos: settings?.notedSerialPos || DEFAULT_POS.notedSerialPos,
    attestedDatePos: settings?.attestedDatePos || DEFAULT_POS.attestedDatePos,
    layout: fullLayout(settings?.layout),
  };
};

let handReady: Promise<void> | null = null;
const HAND_FAMILY = "SjbtAffidavitHand";

const loadHandFont = () => {
  if (!handReady) {
    handReady = new FontFace(HAND_FAMILY, `url(${caveat700})`, { weight: "700" })
      .load()
      .then((loaded) => {
        (document.fonts as any).add(loaded);
      })
      .catch(() => {
        // Falls back to the browser's cursive; try again next time.
        handReady = null;
      });
  }
  return handReady || Promise.resolve();
};

/**
 * `text` drawn on a transparent PNG (4× for print sharpness), trimmed to the
 * text. Drawn here, not typed in the HTML, so it looks the same on screen and
 * in the PDF whatever fonts the PDF server has.
 */
const stampText = (
  text: string,
  style: "stamp" | "hand",
  color: string
): string => {
  if (!text) return "";
  const px = 96;
  const font =
    style === "hand"
      ? `700 ${px}px "${HAND_FAMILY}", "Segoe Script", cursive`
      : `900 ${px}px "Arial Narrow", "Roboto Condensed", Arial, sans-serif`;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.font = font;
  // A rubber date stamp prints narrow and spaced; handwriting runs together.
  const squeeze = style === "stamp" ? 0.78 : 1;
  const width = Math.ceil(ctx.measureText(text).width * squeeze) + px;
  canvas.width = width;
  canvas.height = Math.ceil(px * 1.5);
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textBaseline = "middle";
  ctx.setTransform(squeeze, 0, 0, 1, px / 2, 0);
  if (style === "hand") ctx.rotate(-0.03);
  ctx.fillText(text, 0, canvas.height / 2);
  return canvas.toDataURL("image/png");
};

/** The seal texts of one affidavit; all "" when it has no serial number. */
export const affidavitMarks = async (
  serial?: AffidavitSerial | null
): Promise<AffidavitMarks> => {
  if (!serial?.serialLabel) {
    return { notedDate: "", serial: "", attestedDate: "", footerDate: "" };
  }
  await loadHandFont();
  return {
    notedDate: stampText(notedDateLabel(serial.notedDate), "stamp", STAMP_INK),
    serial: stampText(serial.serialLabel, "hand", PEN_INK),
    // Not written on the attested seal any more (client, 22 Oct).
    attestedDate: "",
    footerDate: footerDateLabel(serial.notedDate),
  };
};
